import { useThemesArray } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useFollowedTopics } from '@terminal/react-query-hooks/topics/useFollowedTopics';
import { useFollowTopic } from '@terminal/react-query-hooks/topics/useFollowTopic';
import { useUnfollowTopic } from '@terminal/react-query-hooks/topics/useUnfollowTopic';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  FollowThemesSearchBar,
  SelectedTheme,
} from '@terminal/views/onboarding/follow-themes/follow-themes-search-bar/FollowThemesSearchBar';
import { ThemeOverviewDrawer } from '@terminal/widgets/theme-widget/theme-overview-drawer/ThemeOverviewDrawer';
import { useState } from 'react';
import styles from './ManageFollowingThemesTab.module.scss';

export const ManageFollowingThemesTab = ({
  trackingSource,
}: {
  trackingSource: PosthogTrackingSource;
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);

  const { trackFollowTheme, trackUnfollowTheme, trackFollowingInsightClicked } =
    usePosthogEvents();
  const { data: followedTopicsData } = useFollowedTopics('theme');
  const followedThemeIds = followedTopicsData?.values ?? [];
  const { data: followedThemes } = useThemesArray(followedThemeIds);

  const { mutate: followTopic } = useFollowTopic();
  const { mutate: unfollowTopic } = useUnfollowTopic();

  const selectedThemes: SelectedTheme[] = followedThemes.map(theme => ({
    ...theme,
    isClickable: 'type' in theme ? theme.type === 'industry' : true,
  }));

  const handleToggle = (theme: SelectedTheme) => {
    const { id: themeId } = theme;
    const isFollowed = followedThemeIds.includes(themeId);

    if (isFollowed) {
      unfollowTopic({ type: 'theme', code: themeId });
      trackUnfollowTheme(trackingSource, themeId);
      if (selectedThemeId === themeId) {
        setSelectedThemeId(null);
      }
    } else {
      followTopic({ type: 'theme', code: themeId });
      trackFollowTheme(trackingSource, themeId);
    }
  };

  const handlePillClick = (theme: SelectedTheme) => {
    if (!theme.isClickable) {
      return;
    }

    const themeMeta = followedThemes.find(({ id }) => id === theme.id);

    if (themeMeta) {
      setSelectedThemeId(themeMeta.id);
      trackFollowingInsightClicked(trackingSource, 'thematic');
    }
  };

  const handleCloseThemeDrawer = () => setSelectedThemeId(null);

  return (
    <div className={styles.root}>
      <FollowThemesSearchBar
        className={styles.searchBar}
        variant="drawer"
        selectedThemes={selectedThemes}
        onToggle={handleToggle}
        onPillClick={selectedTheme => handlePillClick(selectedTheme)}
        isOpen={isSearchOpen}
        onOpenChange={setIsSearchOpen}
      />

      <ThemeOverviewDrawer
        className={styles.themeOverviewDrawer}
        dataTestId="manage-following-theme-drawer"
        isOpen={!!selectedThemeId}
        onClose={handleCloseThemeDrawer}
        themeId={selectedThemeId ?? undefined}
      />
    </div>
  );
};
